(() => {
  const ROOT_SELECTOR = '[data-infinite-scroll-root]';
  const LOAD_MORE_TEXT = 'Load more';
  const LOADING_TEXT = 'Loading...';
  const RETRY_TEXT = 'Try again';
  const LOADED_TEXT = 'More products loaded.';
  const DONE_TEXT = 'All products loaded.';
  const ERROR_TEXT = 'Could not load more products. Please try again.';
  const CONTINUE_TEXT = 'Continue with Load more.';
  const AUTO_LOAD_LIMIT = 0;

  const ensureStyles = () => {
    if (document.getElementById('collection-infinite-scroll-styles')) return;

    const style = document.createElement('style');
    style.id = 'collection-infinite-scroll-styles';
    style.textContent = `
      .collection-infinite-scroll {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        margin-top: 2.4rem;
      }

      .collection-infinite-scroll__button.is-loading {
        position: relative;
        padding-left: 4.2rem;
      }

      .collection-infinite-scroll__button.is-loading::before {
        content: '';
        position: absolute;
        left: 1.6rem;
        top: 50%;
        width: 1.4rem;
        height: 1.4rem;
        margin-top: -0.7rem;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        animation: collection-infinite-scroll-spin 0.75s linear infinite;
      }

      @keyframes collection-infinite-scroll-spin {
        to {
          transform: rotate(360deg);
        }
      }
    `;
    document.head.appendChild(style);
  };

  class CollectionInfiniteScroll {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.loading = false;
      this.nextUrl = null;
      this.didLoadAnyPage = false;
      this.autoLoadsCompleted = 0;
      this.requestSerial = 0;
      this.domVersion = 0;
      this.lastContextKey = null;
      this.intersectionObserver = null;
      this.refresh = this.refresh.bind(this);
      this.handleIntersect = this.handleIntersect.bind(this);
      this.handleMutations = this.handleMutations.bind(this);
      this.mutationObserver = new MutationObserver(this.handleMutations);
      this.mutationObserver.observe(this.root, { childList: true, subtree: true });
      this.refresh();
    }

    handleMutations(mutations) {
      const onlyControlChanged = mutations.every((mutation) => {
        const target = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
        return target?.closest?.('[data-infinite-scroll-control]');
      });

      if (onlyControlChanged) return;

      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(this.refresh, 60);
    }

    refresh() {
      const contextKey = `${window.location.pathname}?${window.location.search}`;
      if (this.lastContextKey !== contextKey) {
        this.lastContextKey = contextKey;
        this.autoLoadsCompleted = 0;
        this.didLoadAnyPage = false;
      }

      this.domVersion += 1;
      this.container = this.root.querySelector('#ProductGridContainer');
      this.grid = this.root.querySelector('#product-grid');
      this.control = this.root.querySelector('[data-infinite-scroll-control]');
      this.button = this.control?.querySelector('.collection-infinite-scroll__button') || null;
      this.status = this.control?.querySelector('.collection-infinite-scroll__status') || null;
      this.paginationWrapper = this.root.querySelector('.pagination-wrapper');

      if (!this.container || !this.grid || !this.control || !this.button || !this.status) {
        return;
      }

      this.nextUrl = this.extractNextUrl();

      this.button.removeEventListener('click', this.manualLoadHandler);
      this.manualLoadHandler = () => this.loadNextPage(true);
      this.button.addEventListener('click', this.manualLoadHandler);
      this.button.textContent = LOAD_MORE_TEXT;
      this.button.classList.remove('is-loading');

      if (this.paginationWrapper) {
        this.paginationWrapper.hidden = true;
      }

      if (!this.nextUrl) {
        this.control.hidden = !this.didLoadAnyPage;
        this.button.hidden = true;
        this.button.disabled = true;
        this.status.textContent = this.didLoadAnyPage ? DONE_TEXT : '';
        this.disconnectObserver();
        return;
      }

      this.control.hidden = false;
      this.button.disabled = false;
      this.button.hidden = false;
      this.status.textContent = '';
      if (this.autoLoadsCompleted >= AUTO_LOAD_LIMIT) {
        this.disconnectObserver();
        this.status.textContent = CONTINUE_TEXT;
      } else {
        this.observeControl();
      }
    }

    detectCurrentPage() {
      const current = this.root.querySelector('.pagination__item--current');
      const page = Number.parseInt(current?.textContent?.trim() || '1', 10);
      return Number.isFinite(page) && page > 0 ? page : 1;
    }

    extractNextUrl() {
      const nextArrow = [...this.root.querySelectorAll('.pagination a[href]')].find((link) =>
        /next/i.test(link.getAttribute('aria-label') || '')
      );
      if (nextArrow) return nextArrow.getAttribute('href');

      const currentPage = this.detectCurrentPage();
      const links = [...this.root.querySelectorAll('.pagination a[href]')];
      let fallback = null;

      for (const link of links) {
        const title = Number.parseInt(link.textContent.trim(), 10);
        if (Number.isFinite(title) && title > currentPage) {
          const href = link.getAttribute('href');
          if (!fallback || title < fallback.page) {
            fallback = { href, page: title };
          }
        }
      }

      return fallback?.href || null;
    }

    observeControl() {
      this.disconnectObserver();
      this.intersectionObserver = new IntersectionObserver(this.handleIntersect, {
        rootMargin: '500px 0px 500px 0px',
      });
      this.intersectionObserver.observe(this.control);
    }

    disconnectObserver() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    }

    handleIntersect(entries) {
      for (const entry of entries) {
        if (entry.isIntersecting && this.autoLoadsCompleted < AUTO_LOAD_LIMIT) {
          this.loadNextPage(false);
          break;
        }
      }
    }

    async loadNextPage(fromButton) {
      if (this.loading || !this.nextUrl || !this.sectionId || !this.grid) return;

      this.loading = true;
      const requestId = ++this.requestSerial;
      const requestDomVersion = this.domVersion;
      const requestGrid = this.grid;
      const requestedUrl = this.nextUrl;
      this.button.disabled = true;
      this.button.textContent = LOADING_TEXT;
      this.button.classList.remove('is-loading');
      this.status.textContent = '';

      try {
        const url = new URL(this.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const html = await response.text();
        if (requestId !== this.requestSerial || requestDomVersion !== this.domVersion || requestGrid !== this.grid || requestedUrl !== this.nextUrl) {
          return;
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newContainer = doc.getElementById('ProductGridContainer');
        const newGrid = newContainer?.querySelector('#product-grid');
        const newItems = newGrid ? [...newGrid.querySelectorAll(':scope > .grid__item')] : [];

        if (!newItems.length) {
          this.nextUrl = null;
          this.control.hidden = true;
          return;
        }

        newItems.forEach((item) => {
          this.grid.appendChild(item);
        });
        this.didLoadAnyPage = true;
        if (!fromButton) {
          this.autoLoadsCompleted += 1;
        }

        const newPagination = newContainer.querySelector('.pagination-wrapper');
        if (this.paginationWrapper && newPagination) {
          this.paginationWrapper.replaceWith(newPagination);
          this.paginationWrapper = newPagination;
          this.paginationWrapper.hidden = true;
        } else if (this.paginationWrapper && !newPagination) {
          this.paginationWrapper.remove();
          this.paginationWrapper = null;
        }

        this.nextUrl = this.extractNextUrl();
        this.button.classList.remove('is-loading');

        if (!this.nextUrl) {
          this.button.hidden = true;
          this.button.disabled = true;
          this.status.textContent = DONE_TEXT;
        } else {
          this.button.textContent = LOAD_MORE_TEXT;
          this.button.disabled = false;
          this.button.hidden = false;
          if (!fromButton && this.autoLoadsCompleted >= AUTO_LOAD_LIMIT) {
            this.disconnectObserver();
            this.status.textContent = CONTINUE_TEXT;
          } else {
            this.status.textContent = fromButton ? LOADED_TEXT : '';
          }
        }
      } catch (error) {
        console.error('Infinite scroll failed:', error);
        this.button.classList.remove('is-loading');
        this.button.textContent = RETRY_TEXT;
        this.status.textContent = ERROR_TEXT;
        this.button.disabled = false;
      } finally {
        this.loading = false;
      }
    }
  }

  const init = () => {
    ensureStyles();
    if (!window.location.pathname.includes('/collections/')) return;

    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (!root.dataset.sectionId?.includes('main-collection-product-grid')) return;
      if (!root.__collectionInfiniteScroll) {
        root.__collectionInfiniteScroll = new CollectionInfiniteScroll(root);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
